import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getLanguage } from '@travel/i18n';
import { useDeviceTypes } from '@travel/traveler-core/hooks/useDeviceType';
import { PROVIDER_LIST_TYPE } from '@travel/traveler-core/types/providerInformation';
import { cx, parseQueryString, stringify } from '@travel/utils';

import SalesPromotionInput from 'components/SalesPromotionInput';
import { SearchFormWithRef } from 'components/SearchForm';
import { SearchFormRef, SearchPanelProps, SubmitParams } from 'components/SearchForm/types';

import { replaceLocation } from 'store/__router/actions';
import { updateAreaName } from 'store/providerList/actions';

import { TAB_ID } from 'constants/providerInfoTabIds';
import paths from 'core/universalRouter/paths';
import usePlaceRedirect from 'hooks/usePlaceRedirect';
import { ProviderInformation } from 'ProviderInformation-Types';
import { SearchFormQuery } from 'ProviderList-Types';
import { savedSearchStore } from 'utils/savedSearch';
import { getBasicRedirectSearchQuery } from 'utils/search';

import HiddenFilterForm, { FilterFormProps } from '../HiddenFilterForm';
import SearchInfoSP from './SearchInfoSP';

import styles from './searchBar.module.scss';

type Props = {
  searchFormRef: React.MutableRefObject<SearchFormRef>;
  className?: string;
  query: SearchFormQuery;
  pageIdentifier: string;
  providerItem: ProviderInformation;
  filterFormProps: FilterFormProps;
  isAutoOpenDatePicker: boolean;
  setIsAutoOpenDatePicker: (autoOpen: boolean) => void;
  activeTabId: string;
  activeListType: string;
};

function SearchBar(props: Props) {
  const {
    searchFormRef,
    className,
    query,
    pageIdentifier,
    providerItem,
    filterFormProps,
    isAutoOpenDatePicker,
    setIsAutoOpenDatePicker,
    activeTabId,
    activeListType,
  } = props;

  const placeRedirect = usePlaceRedirect();
  const dispatch = useDispatch();
  const language = useSelector(getLanguage);

  const { isSP } = useDeviceTypes();

  const onSubmitSearchForm = ({ place, formData }: SubmitParams) => {
    const search = new URLSearchParams(formData as any);
    isSP && search.set('activeTabId', activeTabId || TAB_ID.OVERVIEW);
    search.set('listType', activeListType || PROVIDER_LIST_TYPE.ROOM_LIST);
    const queryString = search.toString();
    const queryObject = parseQueryString(queryString);
    const queryObj = Object.fromEntries(formData.entries());

    if (place.category !== 'PROVIDER') {
      dispatch(updateAreaName(place.name));
    }

    if (place.id !== providerItem.id) {
      // New Place
      const basicQueryString = stringify(getBasicRedirectSearchQuery(queryObject));
      placeRedirect(place, basicQueryString);
    } else {
      // Current provider
      dispatch(
        replaceLocation({
          pathname: paths.providerInfo.pathResolver(place.pathId),
          search: queryString,
          state: { isUpdate: true },
        }),
      );
    }
    savedSearchStore(language, place, queryObj);
  };

  const searchPanel = ({ query, onClick }: SearchPanelProps) => (
    <SearchInfoSP query={query} onClick={onClick} destination={providerItem.name || ''} />
  );
  const onPage = query.startDate || query.endDate ? 'providerInfoDated' : 'providerInfoUndated';
  return (
    <div className={cx(className, styles.wrapper)} data-testid="providerInfo-searchForm-wrapper">
      <SearchFormWithRef
        onPage={onPage}
        ref={searchFormRef}
        pageIdentifier={pageIdentifier}
        currentPlaceName={providerItem.name}
        shouldSubmitOnInputChange={true}
        shouldOpenDatePickerOnSelect={false}
        query={query}
        translateId="Provider_Info.Update"
        searchFormClassName={styles.searchFormContainer}
        isAutoOpenDatePicker={isAutoOpenDatePicker}
        setIsAutoOpenDatePicker={setIsAutoOpenDatePicker}
        onSubmit={onSubmitSearchForm}
        expandPanelClassName={styles.active}
        toggleButton={searchPanel}
        filterForm={<HiddenFilterForm {...filterFormProps} />}
        hiddenInput={<SalesPromotionInput {...query} />}
      />
    </div>
  );
}

export default React.memo(SearchBar);
